import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { ArrowUpDown, Plus, Clock, ArrowLeft, ChevronDown, ChevronRight, X, Trash2 } from 'lucide-react';
import { getAccessToken, capitalizeFirst, getStatusIcon, getTouchpointStatus, updateApi } from "../common/utils";
import { getStakeholder } from "../config/ApiConfig";
import { searchEmployees } from "../services/employeeDirectory";

const TouchpointTrackerNew = () => {
    const [stakeholderInfo, setStakeholderInfo] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortField, setSortField] = useState('customer');
    const [sortDirection, setSortDirection] = useState('asc');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [showStakeholderModal, setShowStakeholderModal] = useState(false);
    const [editingStakeholder, setEditingStakeholder] = useState(null);
    const [stakeholderFormData, setStakeholderFormData] = useState({
        // name: '',
        // role: '',
        // organization: '',
        // email: '',
        // lastTouchpointDate: '',
        // touchpointFrequency: '6',
        // actionItems: [],
        // notes: ''
    });


    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await getStakeholder();
                setStakeholderInfo(data);
            } catch (error) {
                console.error('Error fetching stakeholder data:', error);
                setStakeholderInfo([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredAndSortedTouchpoints = useMemo(() => {

        return stakeholderInfo.flatMap(entity =>
            entity.stakeholders.map(stakeholder => ({
                ...entity,
                ...stakeholder,
                stakeholders: undefined
            })))
            .filter(touchpoint => {
                const searchLower = searchTerm.toLowerCase();
                const isCustomer = touchpoint.gsiPk === 'CUSTOMER';
                const isTeam = touchpoint.gsiPk === 'TEAM';
                const captainNames = touchpoint.members
                    ?.filter(member => member.isCaptain)
                    ?.map(member => member.name?.toLowerCase()) || [];
                const hasMatchingCaptain = captainNames.some(name => name?.includes(searchLower));

                const touchpointStatus = getTouchpointStatus(touchpoint.lastTouchpointDate, touchpoint.touchpointFrequency);
                if (statusFilter !== 'all' && touchpointStatus.label !== statusFilter) return false;

                return (
                    touchpoint.customer.toLowerCase().includes(searchLower) ||
                    (isTeam && touchpoint.team.toLowerCase().includes(searchLower)) ||
                    (isTeam && hasMatchingCaptain) ||
                    (isCustomer && 'leadership'.includes(searchLower)) ||
                    (isCustomer && touchpoint.poc.toLowerCase().includes(searchLower))
                );
            })
            .sort((a, b) => {
                let comparison = 0;
                if (sortField === 'status') {
                    const statusA = getTouchpointStatus(a.lastTouchpointDate, a.touchpointFrequency).label;
                    const statusB = getTouchpointStatus(b.lastTouchpointDate, b.touchpointFrequency).label;
                    const statusOrder = { 'Immediate Attention': 0, 'Due Soon': 1, 'On Track': 2, 'No Contact': 3 };
                    comparison = statusOrder[statusA] - statusOrder[statusB];
                } else if (sortField === 'customer') {
                    comparison = a.customer.localeCompare(b.customer);
                    if (comparison !== 0) return comparison;
                    return (a.team ?? "").localeCompare(b.team ?? "");
                } else {
                    const blankDates = sortDirection === 'asc' ? Infinity : -Infinity;
                    const timeA = a.lastTouchpointDate ? new Date(a.lastTouchpointDate).getTime() : blankDates;
                    const timeB = b.lastTouchpointDate ? new Date(b.lastTouchpointDate).getTime() : blankDates;
                    comparison = timeA - timeB;
                }
                return sortDirection === 'asc' ? comparison : -comparison;
            });
    }, [stakeholderInfo, sortField, sortDirection, statusFilter, searchTerm]);

    const openStakeholderModal = (stakeholder = null) => {
        if (stakeholder) {
            setEditingStakeholder(stakeholder);
            setStakeholderFormData({
                id: stakeholder.id,
                name: stakeholder.name,
                role: stakeholder.role,
                customer: stakeholder.customer,
                email: stakeholder.email || '',
                lastTouchpointDate: stakeholder.lastTouchpointDate || '',
                touchpointFrequency: stakeholder.touchpointFrequency || '6',
                actionItems: stakeholder.actionItems,
                notes: stakeholder.notes || '',
                gsiPk: stakeholder.gsiPk,
                gsiSk: stakeholder.gsiSk,
                poc: stakeholder.poc || ''
            });
        } else {
            setEditingStakeholder(null);
            setStakeholderFormData({
                name: '',
                role: '',
                customer: '',
                email: '',
                lastTouchpointDate: '',
                touchpointFrequency: '6',
                actionItems: [],
                notes: '',
                gsiPk: 'CUSTOMER',
                gsiSk: '',
                poc: ''
            });
        }
        setShowStakeholderModal(true);
    }

    const handleStakeholderSubmit = (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        let stakeholderData;
        const { gsiPk, gsiSk, ...cleanedFormData } = stakeholderFormData;
        const existingEntity = stakeholderInfo.find(entity => entity.gsiPk === gsiPk && entity.gsiSk === gsiSk);
        if (editingStakeholder) {
            stakeholderData = existingEntity
                .stakeholders.map(stakeholder =>
                    stakeholder.id === editingStakeholder.id
                        ? { ...stakeholder, ...cleanedFormData }
                        : stakeholder
                );
        } else {
            const newStakeholder = {
                ...cleanedFormData,
                id: `stakeholder-${Date.now()}`
            };
            if (existingEntity)
                stakeholderData = existingEntity.stakeholders.concat(newStakeholder);
            else
                stakeholderData = [newStakeholder];
        }
        const toastId = toast.loading('Updating...');
        const response = updateApi({ data: { stakeholders: stakeholderData }, customer: stakeholderFormData.customer, pk: gsiSk, gsiPk: gsiPk });
        response.then(() => {
            toast.success('Stakeholder updated successfully!', { id: toastId });
            if (existingEntity) {
                setStakeholderInfo(prev => prev.map(team =>
                    (team.gsiPk === gsiPk && team.gsiSk === gsiSk) ?
                        { ...team, stakeholders: stakeholderData, lastUpdated: updatedTimestamp } : team
                ));
            } else {
                setStakeholderInfo(prev => [...prev, {
                    gsiPk: gsiPk,
                    gsiSk: gsiSk,
                    customer: stakeholderFormData.customer,
                    stakeholders: stakeholderData,
                    lastUpdated: updatedTimestamp
                }]);
            }
            setShowStakeholderModal(false);
        }).catch(error => {
            console.error('Error updating stakeholder:', error);
            toast.error('Stakeholder update failed. Please try again.', { id: toastId });
        });
    };

    const addActionItem = () => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: [...prev.actionItems, '']
        }));
    };

    const updateActionItem = (index, value) => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: prev.actionItems.map((item, i) => i === index ? value : item)
        }));
    };

    const removeActionItem = (index) => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: prev.actionItems.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="min-h-screen py-12">
            <div className="max-w-7xl mx-auto px-4">
                <Link to="/dashboard" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
                    <ArrowLeft className="w-5 h-5 mr-1" />
                    Back to Dashboard
                </Link>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <h1 className="text-3xl font-bold text-[#800000]">Customer Touchpoints</h1>
                    <button
                        onClick={() => openStakeholderModal()}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                    </button>
                </div>

                <div className="bg-white rounded-lg shadow mb-6 p-4">
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex-1 min-w-[200px]">
                            <input
                                type="text"
                                placeholder="Search by customer, team, or point of contact"
                                className="w-full px-4 py-2 border rounded-lg"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-wrap gap-2 min-w-[200px] flex-2">
                            <div className="relative flex-1 min-w-[150px]">
                                <select
                                    className="appearance-none px-4 py-2 pr-10 border rounded-lg bg-white cursor-pointer w-full"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="Immediate Attention">Immediate Attention</option>
                                    <option value="Due Soon">Due Soon</option>
                                    <option value="On Track">On Track</option>
                                    <option value="No Contact">No Contact</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12l-4-4h8l-4 4z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-screen">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid" />
                                <p className="mt-4 text-gray-600">Loading data...</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Customer
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Team
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Point of Contact
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => handleSort('date')}
                                        >
                                            <div className="flex items-center">
                                                Last Touchpoint
                                                <ArrowUpDown className="w-4 h-4 ml-1" />
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center">
                                                Status
                                                <ArrowUpDown className="w-4 h-4 ml-1" />
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Contact Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px', maxWidth: '180px' }}>
                                            Notes
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Manage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredAndSortedTouchpoints.map((stakeholder) => {
                                        const touchpointStatus = getTouchpointStatus(stakeholder.lastTouchpointDate, stakeholder.touchpointFrequency);
                                        return (
                                            <tr key={stakeholder.id}>
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-gray-900">{stakeholder.customer}</div>
                                                    <div className="text-sm text-gray-500">{stakeholder.businessUnit}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {stakeholder.gsiPk === 'CUSTOMER' ? 'Leadership' : stakeholder.team}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {stakeholder.gsiPk === 'CUSTOMER' ? stakeholder.poc : 
                                                        (stakeholder.members?.filter((member) => member.isCaptain).length > 0 
                                                            ? stakeholder.members.filter((member) => member.isCaptain).map(c => c.name).join(', ')
                                                            : "N/A")}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {stakeholder.lastTouchpointDate
                                                        ? new Date(stakeholder.lastTouchpointDate).toLocaleDateString()
                                                        : ""}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div
                                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${touchpointStatus.color}`}
                                                    >
                                                        <touchpointStatus.icon className="w-3 h-3 mr-1" />
                                                        {touchpointStatus.label}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900">{stakeholder.name}</div>
                                                    <div className="text-sm text-gray-500">{stakeholder.role}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900" style={{ minWidth: '120px', maxWidth: '180px' }}>
                                                    {stakeholder.notes ? (
                                                        <div 
                                                            className="break-words"
                                                            style={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                            title={stakeholder.notes.length > 150 ? stakeholder.notes : undefined}
                                                        >
                                                            {stakeholder.notes.length > 150 
                                                                ? `${stakeholder.notes.substring(0, 150)}...`
                                                                : stakeholder.notes
                                                            }
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => openStakeholderModal(stakeholder)}
                                                            className="text-blue-600 hover:text-blue-800"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                    }
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Stakeholder Modal */}
            {showStakeholderModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingStakeholder ? 'Edit Stakeholder' : 'Add New Stakeholder'}
                            </h2>
                            <button
                                onClick={() => setShowStakeholderModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={(e) => handleStakeholderSubmit(e)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={stakeholderFormData.name}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, name: capitalizeFirst(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Designation
                                    </label>
                                    <input
                                        type="text"
                                        value={stakeholderFormData.role}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, role: capitalizeFirst(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Customer
                                    </label>
                                    <Select
                                        isClearable
                                        options={[...new Set(stakeholderInfo.map(client => client.customer))]
                                            .filter(Boolean)
                                            .sort((a, b) => a.localeCompare(b))
                                            .map(customer => ({
                                                label: customer,
                                                value: customer
                                            }))
                                        }
                                        value={stakeholderFormData.customer ? { label: stakeholderFormData.customer, value: stakeholderFormData.customer } : null}
                                        isDisabled={editingStakeholder}
                                        onChange={(selected) => setStakeholderFormData(prev => ({ ...prev, customer: selected ? capitalizeFirst(selected.value) : '', gsiSk: selected ? `${selected.value.toLowerCase()}#_CUSTOMER` : '' }))}
                                        placeholder="Select or enter a customer"
                                        required
                                    />
                                </div>

                                {stakeholderFormData.gsiPk === 'CUSTOMER' && <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Point of Contact
                                    </label>
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions
                                        isClearable
                                        placeholder="Name (type to search)"
                                        noOptionsMessage={() => "No employees found"}
                                        loadOptions={async (inputValue) => {
                                            try {
                                                const results = await searchEmployees(inputValue);
                                                return results.map(e => ({
                                                    value: e.id,
                                                    label: e.name + (e.employeeId ? ` (${e.employeeId})` : ''),
                                                    meta: e
                                                }));
                                            } catch (err) {
                                                return [];
                                            }
                                        }}
                                        onChange={(opt) => {
                                            if (opt && opt.meta) {
                                                setStakeholderFormData(prev => ({ 
                                                    ...prev, 
                                                    poc: capitalizeFirst(opt.meta.name)
                                                }));
                                            } else {
                                                setStakeholderFormData(prev => ({ ...prev, poc: '' }));
                                            }
                                        }}
                                        value={stakeholderFormData.poc ? { 
                                            value: stakeholderFormData.poc, 
                                            label: stakeholderFormData.poc, 
                                            meta: null 
                                        } : null}
                                        required
                                        className="w-full"
                                    />
                                </div>}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={stakeholderFormData.email}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, email: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Touchpoint Date
                                    </label>
                                    <input
                                        type="date"
                                        value={stakeholderFormData.lastTouchpointDate}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, lastTouchpointDate: e.target.value }))}
                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Touchpoint Frequency
                                    </label>
                                    <Select
                                        options={[
                                            { label: 'Every 4 weeks', value: '4' },
                                            { label: 'Every 6 weeks', value: '6' },
                                            { label: 'Every 12 weeks', value: '12' }
                                        ]}
                                        value={stakeholderFormData.touchpointFrequency ? { label: `Every ${stakeholderFormData.touchpointFrequency} weeks`, value: stakeholderFormData.touchpointFrequency } : null}
                                        onChange={(selected) => setStakeholderFormData(prev => ({ ...prev, touchpointFrequency: selected ? selected.value : '4' }))}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mt-6 mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Action Items
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addActionItem}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                        + Add Item
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {stakeholderFormData.actionItems.map((item, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateActionItem(index, capitalizeFirst(e.target.value))}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Action item..."
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeActionItem(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={stakeholderFormData.notes}
                                    onChange={(e) => setStakeholderFormData(prev => ({ ...prev, notes: capitalizeFirst(e.target.value) }))}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => setShowStakeholderModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    {editingStakeholder ? 'Update Stakeholder' : 'Add Stakeholder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TouchpointTrackerNew;